import { INITIATIVE_ROW, SortDirection } from '@/constants/initiative'
import isLocalId from '@/utils/initiative/isLocalId'
import * as R from 'ramda'
import EventBus from '@/EventBus'
import { mapActions } from 'vuex'

/**
 * This mixins contains some of the methods to operate on initiatives
 */

export default {
  computed: {
    openInitiatives() {
      return this.initiatives ? this.initiatives.filter(R.propEq('isClosed', false)) : []
    },
    closedInitiatives() {
      return this.initiatives ? this.initiatives.filter(R.propEq('isClosed', true)) : []
    },
    groupedInitiatives() {
      return [...this.openInitiatives, ...this.closedInitiatives]
    }
  },
  methods: {
    ...mapActions(['getInitiatives']),
    async fetchInitiatives({
      orgId,
      krId,
      includeCustomFields,
      includeComputedFields,
      hasParent,
      orderBy
    }) {
      try {
        const response = await this.getInitiatives({
          orgId,
          krId,
          includeCustomFields,
          includeStandardFields: includeComputedFields,
          hasParent,
          orderBy
        })
        let initiatives = response.results
        if (this.initiatives?.every(x => isLocalId(x.id))) {
          // if user is creating some initiatives before the response, it should be appended
          initiatives = [...initiatives, ...this.initiatives]
        }
        this.initiatives = initiatives
        this.initiativesCopy = initiatives
        return response
      } catch (e) {
        const status = R.path(['response', 'status'], e)
        if (status === 404) {
          EventBus.$emit('SHOW_404')
        }
      }
    },
    /**
     * This replaces *initiativeToUpdate* with coresponding initiative from *initiatives*
     * If initiative is marked done/undone, it refreshes the list
     * @param initiatives
     * @param initiativeToUpdate
     * @returns {f2|f1}
     */
    injectUpdatedInitiative(initiatives, initiativeToUpdate, fetchParams) {
      const initiativeId = initiativeToUpdate.localId || initiativeToUpdate.id
      const initiativeIndex = this.initiatives.findIndex(
        x => x.id === initiativeId || x.localId === initiativeId
      )
      if (this.initiatives[initiativeIndex]['isClosed'] !== initiativeToUpdate.isClosed) {
        // status is changed, refresh the initiatives
        this.fetchInitiatives({
          krId: initiativeToUpdate.keyResultId,
          orgId: initiativeToUpdate.organizationId,
          ...fetchParams
        })
      } else {
        return R.update(initiativeIndex, initiativeToUpdate, this.initiatives)
      }
    },
    async handleInitiativeUpdate(initiative, fetchParams = {}) {
      let updatedInitiatives = this.injectUpdatedInitiative(
        this.initiatives,
        initiative,
        fetchParams
      )
      if (updatedInitiatives) {
        this.initiatives = updatedInitiatives
      }
      updatedInitiatives = this.injectUpdatedInitiative(
        this.initiativesCopy,
        initiative,
        fetchParams
      )
      if (updatedInitiatives) {
        this.initiativesCopy = updatedInitiatives
      }
    },
    deleteInitiatives(initiatives) {
      let hasInitiative = this.initiatives?.some(initiative =>
        initiatives.find(i => i.id === initiative.id)
      )
      const isNotContainedInPayload = initiative => !initiatives.find(i => i.id === initiative.id)
      if (hasInitiative) {
        this.initiatives = this.initiatives.filter(isNotContainedInPayload)
        this.initiativesCopy = this.initiativesCopy.filter(isNotContainedInPayload)
      }
    },
    // TODO: functions - getInitiativeRowVue, cancelInitiativeEditing focusTabPin, getSelectedInitiativeVueComponent and moveInitiative  are available in InitiativesKeyboardNavMixins. Use same
    /**
     * This function accepts a DOM node inside initiative row and it will return the reference of Vue component for the initiative
     * @param node
     * @returns {null|*|null|*}
     */
    getInitiativeRowVue(node, componentName = INITIATIVE_ROW) {
      if (!node) return null
      if (R.pathEq(['__vue__', '$options', 'name'], componentName, node)) {
        return node.__vue__
      } else return this.getInitiativeRowVue(node.parentElement)
    },
    /**
     * This can be used to take user out of edit mode of initiative
     */
    cancelInitiativeEditing() {
      const active = document.activeElement
      if (active) {
        const initiativeVue = this.getInitiativeRowVue(active)
        initiativeVue && initiativeVue.cancelInitiativeEdit()
      }
    },
    /**
     * This focuses the tab-pin of selected row, so that tab-key can take focus to next status checkbox
     * @returns {Vue}
     * @type {Element}
     */
    focusTabPin() {
      const selected = this.$el.querySelector('.initiative-row.selected')
      if (selected) {
        const tabPin = selected.querySelector('.tab-pin')
        tabPin &&
          tabPin.focus({
            preventScroll: true
          })
      }
    },
    /**
     * This function can be used to create random id
     * @returns {string}
     */
    createInitiativeId() {
      return (
        'local-' +
        Math.random()
          .toString()
          .split('.')[1]
      )
    },
    getSelectedInitiativeVueComponent() {
      let selectedItem = this.$el.querySelector('.selected')
      if (selectedItem) {
        return selectedItem.__vue__
      }
    },
    onInitiativeMoved({ initiative, fromKr }) {
      this.fetchInitiatives({ orgId: initiative.organizationId, krId: fromKr })
    },
    getOrderByFromSortParams({ sortBy, sortDirection }) {
      if (sortBy) {
        if (sortBy === 'priority') {
          sortBy = 'impact'
        }
        return `${sortDirection === SortDirection.DESCENDING ? '-' : ''}${sortBy}`
      }
    },
    getSortParamsFromOrderBy(orderBy) {
      let sortedDirection = SortDirection.ASCENDING,
        sortedBy = orderBy
      if (orderBy.startsWith('-')) {
        sortedDirection = SortDirection.DESCENDING
        sortedBy = orderBy.substring(1)
      }
      if (sortedBy === 'impact') {
        sortedBy = 'priority'
      }
      return { sortedBy, sortedDirection }
    }
  }
}
